import type { AnyEntityType } from "@l2a/shared";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import type { NavigateFunction, RouteObject } from "react-router";
import type { PermissionCheck } from "@/lib/permissions";

/**
 * FEATURE REGISTRY CONTRACT
 * =========================
 * Every product area (leads, invoices, projects …) is a folder under
 * `src/features/<key>/` whose `index.tsx` default-exports ONE `FeatureModule`.
 * `src/features/registry.ts` imports them statically and the app shell derives
 * the router, the sidebar, the "+" menu, the ⌘K palette and the settings pages
 * from that list. Nothing else in the shell knows a feature exists.
 *
 *   // src/features/invoices/index.tsx
 *   export default defineFeature({
 *     key: "invoices",
 *     title: "Invoices",
 *     routes: [
 *       { path: "invoices", lazy: () => import("./page") },          // -> /invoices
 *       { path: "invoices/:id", lazy: () => import("./detail") },     // -> /invoices/:id
 *     ],
 *     nav: [{ label: "Invoices", to: "/invoices", icon: ReceiptIndianRupee, group: "finance",
 *             permission: "invoices:read", order: 10,
 *             badgeQueryKey: queryKeys.list("invoice", { status: "overdue" }) }],
 *     quickCreate: [{ key: "invoice", label: "Invoice", icon: ReceiptIndianRupee,
 *                     to: "/invoices?new=1", permission: "invoices:write", shortcut: "i" }],
 *     commands: [{ key: "invoices.overdue", label: "Show overdue invoices",
 *                  icon: AlarmClock, to: "/invoices?status=overdue", group: "actions" }],
 *     settingsSections: [{ key: "invoicing", label: "Invoicing & tax", path: "invoicing",
 *                          element: <InvoiceSettingsPage />, permission: "org:manage" }],
 *   });
 *
 * RULES
 * 1. Route `path`s are RELATIVE (no leading slash) — they are mounted inside the
 *    authenticated `AppShell` at "/". Nav `to` values are ABSOLUTE.
 * 2. Pages are code-split: use react-router's `lazy: () => import("./page")` and
 *    export `Component` (and optionally `ErrorBoundary`) from that module. Never
 *    import a page component into `index.tsx` — that would defeat the split.
 * 3. Canonical paths are fixed by `ENTITY_ROUTES` in @l2a/shared: the API puts
 *    those URLs into search results, notifications and e-mails, so a feature MUST
 *    serve the route for the entities it owns.
 * 4. `permission` only hides UI. The API enforces the same permission server-side.
 */

// ---------------------------------------------------------------------------
// Sidebar navigation
// ---------------------------------------------------------------------------

/** Sidebar sections, in display order. */
export const NAV_GROUPS = [
  { key: "home", label: "Home" },
  { key: "sales", label: "Sales" },
  { key: "clients", label: "Clients" },
  { key: "delivery", label: "Delivery" },
  { key: "finance", label: "Finance" },
  { key: "workspace", label: "Workspace" },
] as const;

export type NavGroupKey = (typeof NAV_GROUPS)[number]["key"];

export const NAV_GROUP_ORDER: Record<NavGroupKey, number> = {
  home: 0,
  sales: 1,
  clients: 2,
  delivery: 3,
  finance: 4,
  workspace: 5,
};

export interface NavItem {
  /** Stable id; defaults to the `to` path. Used for React keys and analytics. */
  key?: string;
  label: string;
  /** Absolute path, e.g. "/invoices". */
  to: string;
  icon: LucideIcon;
  group: NavGroupKey;
  /** Hidden when the session lacks the permission(s). */
  permission?: PermissionCheck;
  permissionMode?: "all" | "any";
  /** Sort position inside the group (ascending, default 100). */
  order?: number;
  /**
   * Query key whose cached value renders as a count badge. The sidebar only
   * READS the cache (it never fetches), so the owning page keeps it fresh.
   * Accepted cached shapes: `number`, `{ count }`, `{ unreadCount }`,
   * `{ overdueCount }`, `{ total }`, `{ items: [] }`.
   */
  badgeQueryKey?: readonly unknown[];
  badgeTone?: "neutral" | "primary" | "danger";
  /** Match the path exactly (used by "/" so it is not active everywhere). */
  end?: boolean;
  /** One line for the ⌘K palette and the collapsed-sidebar tooltip. */
  description?: string;
}

// ---------------------------------------------------------------------------
// Actions (quick create + command palette)
// ---------------------------------------------------------------------------

/** Passed to `run()` so an action can navigate and dismiss its menu. */
export interface ActionContext {
  navigate: NavigateFunction;
  /** Closes the palette / menu that ran the action. */
  close: () => void;
}

interface ActionBase {
  key: string;
  label: string;
  icon: LucideIcon;
  description?: string;
  /** Navigate here when selected. Ignored if `run` is given. */
  to?: string;
  /** Custom behaviour (open a dialog, start a timer …). */
  run?: (ctx: ActionContext) => void;
  permission?: PermissionCheck;
  permissionMode?: "all" | "any";
  order?: number;
  /** Displayed hotkey, e.g. "mod+i" or "g l" (the feature binds it itself). */
  shortcut?: string;
}

/** An entry in the topbar "+" menu and in the palette's Create group. */
export interface QuickCreateItem extends ActionBase {}

export type CommandGroupKey = "navigate" | "create" | "actions" | "help";

/** An extra ⌘K command. "Go to …" commands are derived from `nav` automatically. */
export interface CommandItem extends ActionBase {
  /** Extra search terms (synonyms, abbreviations, Hindi labels …). */
  keywords?: string[];
  group?: CommandGroupKey;
  /**
   * "platform": shown only once GET /api/platform/v1/me has answered 200.
   * Platform access is not a workspace permission, so `permission` cannot
   * express it; an agency owner must never be offered the console.
   */
  audience?: "platform";
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

/**
 * A page under /settings. The settings shell renders the section list and the
 * route `/settings/<path>` for every registered section the user may see.
 */
export interface SettingsSection {
  key: string;
  label: string;
  /** Relative to /settings, e.g. "invoicing" → /settings/invoicing. */
  path: string;
  element: ReactNode;
  permission?: PermissionCheck;
  permissionMode?: "all" | "any";
  /** Grouping heading in the settings sidebar, e.g. "Workspace". */
  group?: string;
  order?: number;
  description?: string;
  icon?: LucideIcon;
}

// ---------------------------------------------------------------------------
// The module
// ---------------------------------------------------------------------------

export interface FeatureModule {
  /** Unique, kebab/lower-case, matches the folder name. */
  key: string;
  /** Human name, used in dev tooling and error messages. */
  title: string;
  /** Entity types this feature owns (documentation + search-result routing). */
  entities?: readonly AnyEntityType[];
  /** Authenticated pages, mounted inside the AppShell at "/". */
  routes?: RouteObject[];
  /** Client-portal pages, mounted inside the branded PortalShell at "/portal". */
  portalRoutes?: RouteObject[];
  /** Unauthenticated pages (public proposal, invoice pay link, form, booking). */
  publicRoutes?: RouteObject[];
  nav?: NavItem[];
  quickCreate?: QuickCreateItem[];
  commands?: CommandItem[];
  settingsSections?: SettingsSection[];
}

/** Identity helper: keeps literal types and gives editors autocomplete. */
export function defineFeature(module: FeatureModule): FeatureModule {
  return module;
}

export function navItemKey(item: NavItem): string {
  return item.key ?? item.to;
}

/** Reads a nav badge count out of whatever the owning page cached. */
export function readBadgeCount(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (value && typeof value === "object") {
    const o = value as { count?: unknown; total?: unknown; items?: unknown; unreadCount?: unknown; overdueCount?: unknown };
    for (const n of [o.count, o.unreadCount, o.overdueCount, o.total]) if (typeof n === "number") return n;
    if (Array.isArray(o.items)) return o.items.length;
  }
  return null;
}
